import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import { en } from '../../translations/en';
import { logger } from '../../utils/logger';
import {
  uploadDocument,
  listVersions,
  formatVersion,
  formatFileSize,
  LegalVersionRecord,
} from '../../utils/legalApi';
import { LegalPdfViewer } from '../LegalPdfViewer';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const t = en.admin.legalDocs;

const LegalDocsTab: React.FC = () => {
  const [tosVersions, setTosVersions] = useState<LegalVersionRecord[]>([]);
  const [privacyVersions, setPrivacyVersions] = useState<LegalVersionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<'tos' | 'privacy' | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'new-upload' | 'view-existing'>('new-upload');
  const [viewingVersion, setViewingVersion] = useState<LegalVersionRecord | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [tosFileError, setTosFileError] = useState<string | null>(null);
  const [privacyFileError, setPrivacyFileError] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    const loadVersions = async () => {
      try {
        const [tosResult, privacyResult] = await Promise.all([
          listVersions('tos'),
          listVersions('privacy'),
        ]);
        setTosVersions(tosResult);
        setPrivacyVersions(privacyResult);
      } catch (err) {
        logger.error('Failed to load legal document versions', err);
        setError(t.errorLoadVersions);
      } finally {
        setLoading(false);
      }
    };

    loadVersions();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (
    docType: 'tos' | 'privacy',
    file: File
  ) => {
    const setFileError = docType === 'tos' ? setTosFileError : setPrivacyFileError;
    setFileError(null);

    if (file.type !== 'application/pdf') {
      setFileError(t.errorFileType);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError(t.errorFileSize);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setSelectedDocType(docType);
    setPreviewUrl(objectUrl);
    setPageCount(0);
    setDialogMode('new-upload');
    setDialogOpen(true);
  };

  const handlePublish = async () => {
    if (!selectedDocType || !selectedFile) return;

    setPublishing(true);
    setError(null);

    try {
      const result = await uploadDocument(selectedDocType, selectedFile);

      // Revoke the object URL before closing
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setDialogOpen(false);
      setSelectedFile(null);
      setSelectedDocType(null);
      setPreviewUrl(null);

      const formattedVersion = formatVersion(result.version);
      setSuccessMessage(
        t.successBanner.replace('{version}', formattedVersion)
      );

      // Auto-clear success message after 5s
      setTimeout(() => setSuccessMessage(null), 5000);

      // Re-fetch version list for the updated doc type
      const updatedVersions = await listVersions(selectedDocType);
      if (selectedDocType === 'tos') {
        setTosVersions(updatedVersions);
      } else {
        setPrivacyVersions(updatedVersions);
      }
    } catch (err) {
      logger.error('Failed to upload legal document', err);
      setError(t.errorUpload);
    } finally {
      setPublishing(false);
    }
  };

  const handleDialogClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setSelectedDocType(null);
    setPreviewUrl(null);
    setViewingVersion(null);
    setDialogOpen(false);
  };

  const handleVersionClick = (version: LegalVersionRecord) => {
    setViewingVersion(version);
    setPageCount(0);
    setDialogMode('view-existing');
    setDialogOpen(true);
  };

  const getNextVersionNumber = (versions: LegalVersionRecord[]): number => {
    if (versions.length > 0) {
      return parseInt(versions[0].version, 10) + 1;
    }
    return 1;
  };

  const renderVersionHistory = (versions: LegalVersionRecord[]) => {
    if (versions.length === 0) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t.emptyStateHeading}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t.emptyStateBody}
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.tableVersion}</TableCell>
              <TableCell>{t.tableEffectiveDate}</TableCell>
              <TableCell>{t.tableFileName}</TableCell>
              <TableCell>{t.tableFileSize}</TableCell>
              <TableCell>{t.tableUploadedBy}</TableCell>
              <TableCell>{t.tableUploadedAt}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((version, index) => (
              <TableRow
                key={version.version}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleVersionClick(version)}
              >
                <TableCell>
                  {formatVersion(version.version)}
                  {index === 0 && (
                    <Chip
                      label={t.currentChip}
                      color="success"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {new Date(version.effectiveDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </TableCell>
                <TableCell>
                  {version.fileName.length > 40
                    ? `${version.fileName.slice(0, 40)}...`
                    : version.fileName}
                </TableCell>
                <TableCell>{formatFileSize(version.fileSize)}</TableCell>
                <TableCell>{version.uploadedBy}</TableCell>
                <TableCell>
                  {new Date(version.uploadedAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDocumentCard = (
    docType: 'tos' | 'privacy',
    title: string,
    versions: LegalVersionRecord[],
    fileError: string | null,
    setFileError: (err: string | null) => void
  ) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            component="label"
          >
            {t.uploadButton}
            <input
              type="file"
              hidden
              accept=".pdf,application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileSelect(docType, file);
                }
                // Reset input so same file can be selected again
                e.target.value = '';
              }}
            />
          </Button>
        </Box>

        {fileError && (
          <Alert
            severity="error"
            onClose={() => setFileError(null)}
            sx={{ mb: 2 }}
          >
            {fileError}
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          {t.versionHistoryHeading}
        </Typography>

        {renderVersionHistory(versions)}
      </CardContent>
    </Card>
  );

  const getDocTypeName = (docType: 'tos' | 'privacy' | null): string => {
    if (docType === 'tos') return t.termsTitle;
    if (docType === 'privacy') return t.privacyTitle;
    return '';
  };

  const getDialogTitle = (): string => {
    if (dialogMode === 'new-upload' && selectedDocType) {
      const nextVersion = getNextVersionNumber(
        selectedDocType === 'tos' ? tosVersions : privacyVersions
      );
      return t.dialogTitleNewUpload
        .replace('{docType}', getDocTypeName(selectedDocType))
        .replace('{version}', String(nextVersion));
    }
    if (dialogMode === 'view-existing' && viewingVersion) {
      return t.dialogTitleExisting
        .replace('{docType}', getDocTypeName(viewingVersion.documentType as 'tos' | 'privacy'))
        .replace('{version}', formatVersion(viewingVersion.version));
    }
    return '';
  };

  const getFileMetadata = (): string => {
    if (dialogMode === 'new-upload' && selectedFile) {
      return t.fileMetadataFormat
        .replace('{fileName}', selectedFile.name)
        .replace('{size}', formatFileSize(selectedFile.size))
        .replace('{pageCount}', String(pageCount));
    }
    if (dialogMode === 'view-existing' && viewingVersion) {
      return t.fileMetadataFormat
        .replace('{fileName}', viewingVersion.fileName)
        .replace('{size}', formatFileSize(viewingVersion.fileSize))
        .replace('{pageCount}', String(pageCount));
    }
    return '';
  };

  const getPdfUrl = (): string => {
    if (dialogMode === 'new-upload') return previewUrl ?? '';
    if (dialogMode === 'view-existing') return viewingVersion?.presignedUrl ?? '';
    return '';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {renderDocumentCard('tos', t.termsTitle, tosVersions, tosFileError, setTosFileError)}
        {renderDocumentCard(
          'privacy',
          t.privacyTitle,
          privacyVersions,
          privacyFileError,
          setPrivacyFileError
        )}
      </Box>

      <Dialog maxWidth="lg" fullWidth open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>
          <Box>
            <Typography variant="h6">{getDialogTitle()}</Typography>
            <Typography variant="caption" color="text.secondary">
              {getFileMetadata()}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '80vh', overflow: 'hidden' }}>
          {getPdfUrl() && (
            <LegalPdfViewer url={getPdfUrl()} onPageCountChange={setPageCount} />
          )}
        </DialogContent>
        <DialogActions>
          {dialogMode === 'new-upload' ? (
            <>
              <Button onClick={handleDialogClose}>{t.discardUpload}</Button>
              <Button
                variant="contained"
                onClick={handlePublish}
                disabled={publishing}
                startIcon={
                  publishing ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                {publishing ? t.publishButtonLoading : t.publishButton}
              </Button>
            </>
          ) : (
            <Button onClick={handleDialogClose}>{t.closePreview}</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegalDocsTab;
